import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Check, Search, X } from 'lucide-react'
import {
  Button,
  Card,
  ErrorState,
  Input,
  PageHeader,
  Select,
  Textarea,
} from '@/components/ui'
import { cn } from '@/lib/utils.ts'
import { customerApi } from '../lib/api'
import {
  AppointmentBookingForm,
  DEFAULT_APPOINTMENT_VALUE,
  slotToAppointmentPayload,
  type AppointmentBookingValue,
} from '../components/AppointmentBookingForm'

type FollowUpKind = 'none' | 'callback' | 'appointment'

interface ExistingTicket {
  id: number
  nr: string | null
  subject: string
  status: string
}

export function CustomerTicketCreatePage() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const followUp: FollowUpKind =
    params.get('followUp') === 'callback' ? 'callback' :
    params.get('followUp') === 'appointment' ? 'appointment' : 'none'

  // Bei followUp=appointment oder followUp=callback bezieht sich die Aktion
  // auf ein bestehendes Ticket – der Kunde waehlt eines aus der Liste.
  // Bei followUp=none wird ein neues Ticket angelegt.
  const isExistingFlow = followUp !== 'none'

  const [existingTickets, setExistingTickets] = useState<ExistingTicket[]>([])
  const [existingLoading, setExistingLoading] = useState(false)
  const [existingError, setExistingError] = useState<string | null>(null)
  const [existingLoaded, setExistingLoaded] = useState(false)
  const [selectedTicketId, setSelectedTicketId] = useState<string>('')

  // Felder „Neues Ticket"
  const [subject, setSubject] = useState('')
  const [description, setDescription] = useState('')
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium')

  // Felder „Rueckruf"
  const [phone, setPhone] = useState('')
  const [preferredTime, setPreferredTime] = useState('')

  // Felder „Termin" (Dauer / Ort / Slot / Anmerkung gebuendelt)
  const [appointment, setAppointment] = useState<AppointmentBookingValue>(DEFAULT_APPOINTMENT_VALUE)

  const [submitting, setSubmitting] = useState(false)
  // Server-Fehler beim eigentlichen Submit (TANSS/Netzwerk). Validierungs-
  // Fehler kommen NICHT hier rein, sondern als Field-Errors (siehe unten).
  const [error, setError] = useState<string | null>(null)
  const [errorDetails, setErrorDetails] = useState<string | null>(null)

  // Feld-spezifische Fehler — werden im jeweiligen Input direkt unter dem
  // Feld angezeigt, statt als grosser Banner oben.
  type FieldKey = 'subject' | 'phone' | 'ticket' | 'slot' | 'note'
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldKey, string>>>({})
  const clearFieldError = (key: FieldKey) => {
    setFieldErrors((prev) => {
      if (!prev[key]) return prev
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  // Bestehende Tickets laden (nur wenn wir im existing-Flow sind)
  useEffect(() => {
    if (!isExistingFlow || existingLoaded || existingLoading) return
    setExistingLoading(true)
    setExistingError(null)
    customerApi.get<{ data: ExistingTicket[] }>('/tickets')
      .then((res) => setExistingTickets(res.data?.data ?? []))
      .catch((e) => setExistingError(e instanceof Error ? e.message : 'Tickets konnten nicht geladen werden'))
      .finally(() => { setExistingLoading(false); setExistingLoaded(true) })
  }, [isExistingFlow, existingLoaded, existingLoading])

  const submit = async () => {
    setError(null)
    setErrorDetails(null)

    // Feld-Validierung: alle Fehler aufsammeln und pro Feld anzeigen. Kein
    // globaler "Fehler beim Laden"-Banner fuer Pflichtfeld-Verletzungen.
    const errs: Partial<Record<FieldKey, string>> = {}
    if (isExistingFlow && !selectedTicketId) {
      errs.ticket = 'Bitte ein Ticket auswählen'
    }
    if (!isExistingFlow && !subject.trim()) {
      errs.subject = 'Betreff ist Pflicht'
    }
    if (followUp === 'callback' && !phone.trim()) {
      errs.phone = 'Telefonnummer ist Pflicht'
    }
    if (followUp === 'appointment' && !appointment.slot) {
      errs.slot = 'Bitte einen Termin-Slot auswählen'
    }
    if (followUp === 'appointment' && !appointment.note.trim()) {
      errs.note = 'Bitte eine kurze Anmerkung angeben'
    }
    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs)
      return
    }
    setFieldErrors({})

    setSubmitting(true)
    try {
      if (isExistingFlow) {
        const id = selectedTicketId
        if (followUp === 'callback') {
          await customerApi.post(`/tickets/${id}/callback`, {
            phone: phone.trim(),
            preferredTime: preferredTime.trim() || undefined,
          })
        } else if (followUp === 'appointment' && appointment.slot) {
          await customerApi.post(
            `/tickets/${id}/appointment`,
            slotToAppointmentPayload(appointment.slot, appointment.location, appointment.note),
          )
        }
        navigate(`/tickets/${id}`)
      } else {
        const body: Record<string, unknown> = { subject, description, priority }
        const res = await customerApi.post<{ ticket: { id: number } }>('/tickets', body)
        const ticketId = res.data?.ticket?.id
        navigate(ticketId ? `/tickets/${ticketId}` : '/tickets')
      }
    } catch (e) {
      const ax = e as {
        response?: { status?: number; data?: { error?: string; message?: string; details?: unknown } }
        config?: { method?: string; url?: string; data?: unknown }
        message?: string
      }
      const status = ax.response?.status
      const baseMsg = ax.response?.data?.error ?? ax.response?.data?.message ?? ax.message ?? 'Senden fehlgeschlagen'
      setError(status ? `${baseMsg} (HTTP ${status})` : baseMsg)
      setErrorDetails(
        JSON.stringify(
          {
            request: { method: ax.config?.method?.toUpperCase(), url: ax.config?.url, body: ax.config?.data },
            responseStatus: status,
            responseBody: ax.response?.data,
            axiosMessage: ax.message,
          },
          null,
          2,
        ),
      )
    } finally {
      setSubmitting(false)
    }
  }

  const { title, description: headerDescription, submitLabel } = headerFor(followUp)

  return (
    <div>
      <PageHeader title={title} description={headerDescription} />
      <Card>
        <Card.Body>
          {error ? (
            <ErrorState
              size="sm"
              description={error}
              details={errorDetails ?? undefined}
              className="mb-3"
            />
          ) : null}

          {/* ─── Existing-Flow: Ticket-Auswahl (suchbar) ─── */}
          {isExistingFlow && (
            <div className="mb-4">
              {existingLoading && <div className="text-body text-gray-500">Tickets werden geladen …</div>}
              {existingError && <ErrorState size="sm" description={existingError} />}
              {!existingLoading && !existingError && existingLoaded && existingTickets.length === 0 && (
                <div className="text-body text-gray-500">
                  Du hast aktuell keine Tickets – bitte zuerst ein neues Ticket anlegen.
                </div>
              )}
              {existingTickets.length > 0 && (
                <TicketCombobox
                  tickets={existingTickets}
                  value={selectedTicketId}
                  onChange={(id) => { setSelectedTicketId(id); clearFieldError('ticket') }}
                  error={fieldErrors.ticket}
                />
              )}
            </div>
          )}

          {/* ─── Neues Ticket: Betreff/Beschreibung/Prioritaet ─── */}
          {!isExistingFlow && (
            <div className="space-y-3">
              <Input
                label="Betreff"
                value={subject}
                onChange={(e) => { setSubject(e.target.value); clearFieldError('subject') }}
                error={fieldErrors.subject}
                required
              />
              <Textarea label="Beschreibung" rows={8} value={description} onChange={(e) => setDescription(e.target.value)} />
              <Select<'low' | 'medium' | 'high'>
                label="Priorität"
                value={priority}
                onValueChange={setPriority}
                options={[
                  { value: 'low', label: 'Niedrig' },
                  { value: 'medium', label: 'Normal' },
                  { value: 'high', label: 'Hoch' },
                ]}
              />
            </div>
          )}

          {/* ─── Rueckruf-Felder ─── */}
          {followUp === 'callback' && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input
                label="Telefonnummer"
                value={phone}
                onChange={(e) => { setPhone(e.target.value); clearFieldError('phone') }}
                error={fieldErrors.phone}
                required
              />
              <Input
                label="Wunschzeitraum (optional)"
                value={preferredTime}
                onChange={(e) => setPreferredTime(e.target.value)}
                placeholder="z. B. heute 14–16 Uhr"
              />
            </div>
          )}

          {/* ─── Termin-Felder ─── */}
          {followUp === 'appointment' && (
            <div className="mt-4">
              <AppointmentBookingForm
                ticketId={selectedTicketId || null}
                value={appointment}
                onChange={(next) => {
                  setAppointment(next)
                  if (next.slot) clearFieldError('slot')
                  if (next.note.trim()) clearFieldError('note')
                }}
                errors={{ slot: fieldErrors.slot, note: fieldErrors.note }}
                noteRequired
              />
            </div>
          )}

          <div className="flex justify-end gap-2 mt-4">
            <Button variant="ghost" onClick={() => navigate('/tickets')}>Abbrechen</Button>
            <Button variant="primary" onClick={submit} loading={submitting}>
              {followUp === 'appointment'
                ? appointment.location === 'onsite' ? 'Termin anfragen' : 'Termin buchen'
                : submitLabel}
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  )
}

// ─── Ticket-Combobox ─────────────────────────────────────────────────────────
// Einfache suchbare Auswahl (Tippen ermoeglicht), ohne externe Lib. Eingabe
// oben, darunter eine gefilterte Liste klickbarer Tickets. Ist ein Ticket
// gewaehlt, wird sein Label im Input angezeigt und kann ueber das X geloescht
// werden, um erneut zu suchen.
function TicketCombobox({
  tickets,
  value,
  onChange,
  error,
}: {
  tickets: ExistingTicket[]
  value: string
  onChange: (id: string) => void
  error?: string
}) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)

  const selectedTicket = useMemo(
    () => tickets.find((t) => String(t.id) === value) ?? null,
    [tickets, value],
  )

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return tickets.slice(0, 50)
    return tickets
      .filter((t) => {
        const label = `${t.nr ?? t.id} ${t.subject ?? ''}`.toLowerCase()
        return label.includes(q)
      })
      .slice(0, 50)
  }, [tickets, query])

  // Klick ausserhalb schliesst die Dropdown-Liste
  useEffect(() => {
    if (!open) return
    function onDocClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [open])

  function pick(t: ExistingTicket) {
    onChange(String(t.id))
    setQuery('')
    setOpen(false)
  }

  function clear() {
    onChange('')
    setQuery('')
    setOpen(true)
  }

  const showInput = !selectedTicket || open
  const showSelectedChip = selectedTicket && !open

  return (
    <div ref={containerRef} className="relative">
      <label className="block text-small font-medium text-gray-700 mb-1">Ticket auswählen</label>

      {showSelectedChip ? (
        <button
          type="button"
          onClick={() => { setOpen(true); setQuery('') }}
          className={cn(
            'w-full flex items-center justify-between gap-2 rounded-md border bg-white px-3 py-2 text-left text-body text-gray-900 focus:outline-none focus-visible:ring-2',
            error
              ? 'border-red-400 focus-visible:ring-red-400'
              : 'border-gray-300 hover:border-gray-400 focus-visible:ring-brand-500',
          )}
        >
          <span className="truncate">
            <span className="font-mono text-small font-medium text-gray-700 mr-2">
              #{selectedTicket.nr || selectedTicket.id}
            </span>
            {selectedTicket.subject || '(kein Betreff)'}
          </span>
          <span
            role="button"
            tabIndex={0}
            aria-label="Auswahl aufheben"
            onClick={(e) => { e.stopPropagation(); clear() }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); clear() } }}
            className="p-1 rounded hover:bg-gray-100 text-gray-500"
          >
            <X className="w-4 h-4" />
          </span>
        </button>
      ) : null}

      {showInput ? (
        <Input
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
          onFocus={() => setOpen(true)}
          placeholder="Tippen, um Ticket zu suchen (Betreff oder Nr.)"
          leadingIcon={<Search className="w-4 h-4" />}
          autoFocus={!selectedTicket}
        />
      ) : null}

      {open && (
        <div className="absolute z-popover left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-popover">
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-small text-gray-500">Keine Treffer</div>
          ) : (
            filtered.map((t) => {
              const isSelected = String(t.id) === value
              return (
                <button
                  key={t.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => pick(t)}
                  className={cn(
                    'w-full text-left px-3 py-2 flex items-center justify-between gap-2 text-body transition',
                    isSelected ? 'bg-brand-50 text-brand-800' : 'hover:bg-gray-50 text-gray-800',
                  )}
                >
                  <span className="truncate">
                    <span className="font-mono text-small font-medium text-gray-700 mr-2">
                      #{t.nr || t.id}
                    </span>
                    {t.subject || '(kein Betreff)'}
                  </span>
                  {isSelected && <Check className="w-4 h-4 shrink-0 text-brand-700" />}
                </button>
              )
            })
          )}
        </div>
      )}
      {error ? <div className="mt-1 text-caption text-red-600">{error}</div> : null}
    </div>
  )
}

function headerFor(followUp: FollowUpKind): { title: string; description: string; submitLabel: string } {
  switch (followUp) {
    case 'appointment':
      return {
        title: 'Termin vereinbaren',
        description: 'Wähle das Ticket und einen freien Slot beim zuständigen Techniker.',
        submitLabel: 'Termin anfragen',
      }
    case 'callback':
      return {
        title: 'Rückruf anfordern',
        description: 'Wähle das Ticket, zu dem dein Ansprechpartner zurückrufen soll.',
        submitLabel: 'Rückruf anfordern',
      }
    default:
      return {
        title: 'Neues Ticket',
        description: 'Beschreibe dein Anliegen möglichst präzise.',
        submitLabel: 'Ticket senden',
      }
  }
}

